import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';

const Careers = () => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#2d3748',
      lineHeight: 1.6,
      overflowX: 'hidden'
    }}>
      <Helmet>
        <title>Careers at SpireNest | Join Our NetSuite Team</title>
        <link rel="canonical" href="https://spirenest.co/careers" />
        <meta name="description" content="Explore career opportunities at SpireNest. Join our team of NetSuite experts and help businesses transform with innovative ERP solutions." />
        <meta name="keywords" content="SpireNest careers, NetSuite jobs, ERP careers, USA, technology jobs" />
      </Helmet>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        padding: '140px 0 100px',
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #3b82f6 100%)',
        overflow: 'hidden',
        minHeight:'87vh'
      }}>
        {/* Enhanced Animated background elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          {/* Large floating orb */}
          <motion.div style={{
            position: 'absolute',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 50%, transparent 100%)',
            borderRadius: '50%',
            top: '-250px',
            right: '-250px',
            filter: 'blur(1px)'
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          />
          
          {/* Medium floating orb */}
          <motion.div style={{
            position: 'absolute',
            width: '350px',
            height: '350px',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, rgba(99, 102, 241, 0.08) 50%, transparent 100%)',
            borderRadius: '50%',
            bottom: '-175px',
            left: '-175px',
            filter: 'blur(2px)'
          }}
          animate={{
            y: [0, 40, 0],
            x: [0, -25, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
          />

          {/* Small accent orb */}
          <motion.div style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            background: 'rgba(34, 197, 94, 0.12)',
            borderRadius: '50%',
            top: '20%',
            left: '10%',
            filter: 'blur(1px)'
          }}
          animate={{
            y: [0, -15, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          />

          {/* Geometric shapes */}
          <motion.div style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            background: 'rgba(245, 158, 11, 0.15)',
            borderRadius: '12px',
            top: '30%',
            right: '15%',
            transform: 'rotate(45deg)'
          }}
          animate={{
            rotate: [45, 135, 45],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          />
        </div>
        
        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="text-center text-white">
            <Col lg={10} className="mx-auto">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                {/* Enhanced badge */}
                <motion.div variants={fadeInUp} className="mb-4">
                  <Badge 
                    style={{ 
                      fontSize: '0.95rem', 
                      padding: '0.6rem 1.8rem',
                      borderRadius: '50px',
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      fontWeight: 500,
                      letterSpacing: '0.5px'
                    }}
                  >
                    <i className="bi bi-briefcase me-2"></i>
                    1 Open Position Available
                  </Badge>
                </motion.div>

                <motion.h1 
                  variants={fadeInUp} 
                  style={{ 
                    fontSize: 'clamp(2.5rem, 5vw, 4rem)', 
                    fontWeight: 800, 
                    marginBottom: '2rem',
                    textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    color: 'white',
                    lineHeight: 1.2
                  }}
                >
                  Join Our Team
                </motion.h1>

                <motion.p 
                  variants={fadeInUp} 
                  style={{ 
                    fontSize: '1.4rem', 
                    marginBottom: '3rem',
                    opacity: 0.95,
                    maxWidth: '700px',
                    margin: '0 auto 3rem',
                    lineHeight: 1.6,
                    fontWeight: 300
                  }}
                >
                  Build your career with <span style={{ fontWeight: 600, color: '#60a5fa' }}>SpireNest</span> and help businesses transform through innovative NetSuite solutions
                </motion.p>

                {/* Enhanced CTA buttons */}
                <motion.div variants={fadeInUp} className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      size="lg"
                      href="https://forms.office.com/r/XMsY5Aepfv"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: 'linear-gradient(135deg, #ff7a00 0%, #f59e0b 100%)',
                        border: 'none',
                        padding: '1rem 2.5rem',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: '50px',
                        textDecoration: 'none',
                        boxShadow: '0 8px 32px rgba(255, 122, 0, 0.4)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <i className="bi bi-send me-2"></i>
                      Apply Now
                    </Button>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline-light"
                      size="lg"
                      style={{
                        padding: '1rem 2.5rem',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: '50px',
                        borderWidth: '2px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => {
                        document.getElementById('job-details').scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'start' 
                        });
                      }}
                    >
                      <i className="bi bi-info-circle me-2"></i>
                      View Details
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Current Openings Section */}
      <section id="job-details" style={{ padding: '100px 0', background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }} ref={ref}>
        <Container>
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {/* Enhanced Section Header */}
            <motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '5rem' }}>
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                padding: '0.5rem 1.5rem',
                borderRadius: '50px',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: 600,
                marginBottom: '2rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                <i className="bi bi-briefcase me-2"></i>
                Open Positions
              </div>
              
              <h2 style={{ 
                fontWeight: 800, 
                marginBottom: '1.5rem', 
                color: '#1e293b',
                fontSize: 'clamp(2.2rem, 4vw, 3.2rem)',
                background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Current Openings
              </h2>
              
              <p style={{ 
                fontSize: '1.3rem', 
                color: '#64748b',
                maxWidth: '650px',
                margin: '0 auto',
                lineHeight: 1.7,
                fontWeight: 300
              }}>
                Explore exciting career opportunities and become part of our growing team
              </p>
            </motion.div>

            <Row className="justify-content-center ">
              <Col lg={10}>
                <motion.div variants={fadeInUp}>
                  <Card 
                    style={{
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                    whileHover={{ 
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
                      y: -5 
                    }}
                  >
                    {/* Enhanced Job Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                      padding: '2.5rem',
                      color: 'white',
                      position: 'relative'
                    }}>
                      {/* Background pattern */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '200px',
                        height: '200px',
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%'
                      }}></div>

                      <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 1 }}>
                        <div className="d-flex align-items-center">
                          <div style={{
                            width: '60px',
                            height: '60px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1.5rem',
                            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
                          }}>
                            <i className="bi bi-person-workspace" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                          <div>
                            <h3 style={{ 
                              fontWeight: 700, 
                              marginBottom: '0.5rem',
                              fontSize: '2rem',
                              color: 'white'
                            }}>
                              Remote SDR / BDR
                            </h3>
                            <p style={{ 
                              opacity: 0.9,
                              margin: 0, 
                              fontSize: '1.1rem',
                              color: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              Sales Development Representative
                            </p>
                          </div>
                        </div>
                        
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          style={{
                            width: '45px',
                            height: '45px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '1rem',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          <i className="bi bi-chevron-down" style={{ 
                            color: 'white',
                            fontSize: '1.3rem'
                          }}></i>
                        </motion.div>
                      </div>

                      {/* Job meta information */}
                      <div className="mt-4" style={{ position: 'relative', zIndex: 1 }}>
                        <div className="d-flex flex-wrap gap-3">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-geo-alt me-2" style={{ color: '#60a5fa' }}></i>
                            <span>Dublin, CA</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-briefcase me-2" style={{ color: '#34d399' }}></i>
                            <span>Full-time Contract</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-building me-2" style={{ color: '#fbbf24' }}></i>
                            <span>On-site</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar-date me-2" style={{ color: '#f87171' }}></i>
                            <span>Immediate Start</span>
                          </div>
                        </div>
                      </div>
                      
                      {!isExpanded && (
                        <div className="mt-4" style={{ position: 'relative', zIndex: 1 }}>
                          <span style={{
                            background: 'rgba(255, 255, 255, 0.15)',
                            color: 'white',
                            padding: '0.6rem 1.5rem',
                            borderRadius: '25px',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>
                            <i className="bi bi-eye me-2"></i>
                            Click to view details
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Expandable Card Body */}
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ 
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0
                      }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      <Card.Body style={{ padding: '2rem 2rem 3rem' }}>
                      {/* Enhanced Job Description */}
                      <div className="mb-5">
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '2rem',
                          paddingBottom: '1rem',
                          borderBottom: '2px solid #f1f5f9'
                        }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1rem'
                          }}>
                            <i className="bi bi-file-text text-white" style={{ fontSize: '1.3rem' }}></i>
                          </div>
                          <h4 style={{ 
                            fontWeight: 700, 
                            color: '#1e293b',
                            margin: 0,
                            fontSize: '1.5rem'
                          }}>
                            Job Description
                          </h4>
                        </div>
                        
                        <div style={{
                          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                          padding: '2rem',
                          borderRadius: '16px',
                          borderLeft: '4px solid #3b82f6'
                        }}>
                          <p style={{ 
                            fontSize: '1.2rem', 
                            color: '#374151',
                            lineHeight: '1.8',
                            margin: 0,
                            fontWeight: 400
                          }}>
                            This is a contract on-site role located in <strong>Dublin, CA</strong> for a Sales Development Representative (SDR) / 
                            Business Development Representative (BDR). The SDR/BDR will be responsible for day-to-day tasks including 
                            lead generation, developing and nurturing leads, initiating outbound calls and emails, managing the sales 
                            pipeline, and setting up appointments for the sales team. They will also be responsible for building and 
                            maintaining strong customer relationships.
                          </p>
                        </div>
                      </div>

                      {/* Enhanced Key Responsibilities */}
                      <div className="mb-5">
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '2rem',
                          paddingBottom: '1rem',
                          borderBottom: '2px solid #f1f5f9'
                        }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1rem'
                          }}>
                            <i className="bi bi-list-check text-white" style={{ fontSize: '1.3rem' }}></i>
                          </div>
                          <h4 style={{ 
                            fontWeight: 700, 
                            color: '#1e293b',
                            margin: 0,
                            fontSize: '1.5rem'
                          }}>
                            Key Responsibilities
                          </h4>
                        </div>

                        <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                          {[
                            'Lead generation and prospecting',
                            'Developing and nurturing leads',
                            'Initiating outbound calls and emails',
                            'Managing the sales pipeline',
                            'Setting up appointments for the sales team',
                            'Building and maintaining strong customer relationships'
                          ].map((responsibility, index) => (
                            <motion.li 
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                              style={{
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '1.15rem',
                                color: '#374151',
                                padding: '0.75rem',
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease'
                              }}
                              whileHover={{
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              }}
                            >
                              <i className="bi bi-check-circle-fill me-3" style={{ 
                                color: '#10b981', 
                                fontSize: '1.2rem',
                                flexShrink: 0
                              }}></i>
                              {responsibility}
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      {/* Enhanced Qualifications */}
                      <div className="mb-5">
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '2rem',
                          paddingBottom: '1rem',
                          borderBottom: '2px solid #f1f5f9'
                        }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1rem'
                          }}>
                            <i className="bi bi-mortarboard text-white" style={{ fontSize: '1.3rem' }}></i>
                          </div>
                          <h4 style={{ 
                            fontWeight: 700, 
                            color: '#1e293b',
                            margin: 0,
                            fontSize: '1.5rem'
                          }}>
                            Qualifications & Requirements
                          </h4>
                        </div>

                        <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                          {[
                            'Inside Sales, Sales, and Business Development experience',
                            'Proficiency in Lead Generation and nurturing',
                            'Strong Communication skills',
                            'Excellent interpersonal and negotiation skills',
                            'Ability to work independently and as part of a team',
                            'Previous experience in ERP implementation or related fields is a plus',
                            'Bachelor\'s degree in Business, Marketing, or related field'
                          ].map((qualification, index) => (
                            <motion.li 
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                              style={{
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '1.15rem',
                                color: '#374151',
                                padding: '0.75rem',
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease'
                              }}
                              whileHover={{
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              }}
                            >
                              <i className="bi bi-star-fill me-3" style={{ 
                                color: '#f59e0b', 
                                fontSize: '1.2rem',
                                flexShrink: 0
                              }}></i>
                              {qualification}
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      {/* Enhanced Apply Button */}
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '3rem 0 1rem',
                        borderTop: '3px solid #f1f5f9',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.02) 0%, rgba(139, 92, 246, 0.02) 100%)',
                        borderRadius: '20px',
                        marginTop: '2rem'
                      }}>
                        <h4 style={{
                          color: '#1e293b',
                          fontWeight: 700,
                          marginBottom: '1.5rem',
                          fontSize: '1.4rem'
                        }}>
                          Ready to Apply?
                        </h4>
                        
                        <p style={{ 
                          color: '#64748b',
                          fontSize: '1.1rem',
                          marginBottom: '2rem',
                          maxWidth: '500px',
                          margin: '0 auto 2rem'
                        }}>
                          Join our team and help businesses transform with innovative NetSuite solutions
                        </p>

                        <motion.div
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            size="lg"
                            href="https://forms.office.com/r/XMsY5Aepfv"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: 'linear-gradient(135deg, #ff7a00 0%, #f59e0b 100%)',
                              border: 'none',
                              padding: '1.2rem 3rem',
                              fontSize: '1.2rem',
                              fontWeight: 700,
                              borderRadius: '50px',
                              textDecoration: 'none',
                              boxShadow: '0 12px 35px rgba(255, 122, 0, 0.4)',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <span style={{ position: 'relative', zIndex: 1 }}>
                              <i className="bi bi-send me-2"></i>
                              Apply Now
                            </span>
                          </Button>
                        </motion.div>
                        
                        <p style={{ 
                          marginTop: '1.5rem', 
                          color: '#64748b',
                          fontSize: '0.95rem'
                        }}>
                          <i className="bi bi-shield-check me-1" style={{ color: '#10b981' }}></i>
                          Your application will be reviewed within 24 hours
                        </p>
                      </div>
                    </Card.Body>
                    </motion.div>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Why Join Us Section */}
      <section style={{ padding: '80px 0', backgroundColor: 'white' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Row className="text-center mb-5">
              <Col lg={8} className="mx-auto">
                <h2 style={{ 
                  fontWeight: 800, 
                  marginBottom: '1rem', 
                  color: '#1e293b',
                  fontSize: '2.5rem'
                }}>
                  Why Join SpireNest?
                </h2>
                <p style={{ 
                  fontSize: '1.2rem', 
                  color: '#64748b'
                }}>
                  Be part of a team that's transforming businesses through innovative NetSuite solutions
                </p>
              </Col>
            </Row>

            <Row className="g-4">
              {[
                {
                  icon: 'bi-rocket',
                  title: 'Growth Opportunities',
                  description: 'Advance your career in the rapidly growing ERP and CRM industry'
                },
                {
                  icon: 'bi-people',
                  title: 'Collaborative Team',
                  description: 'Work with experienced professionals who support your success'
                },
                {
                  icon: 'bi-award',
                  title: 'Competitive Benefits',
                  description: 'Enjoy comprehensive benefits and competitive compensation'
                },
                {
                  icon: 'bi-lightbulb',
                  title: 'Innovation Focus',
                  description: 'Work on cutting-edge projects with industry-leading technology'
                }
              ].map((benefit, index) => (
                <Col md={6} lg={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    style={{ height: '100%' }}
                  >
                    <Card style={{
                      height: '100%',
                      border: 'none',
                      borderRadius: '15px',
                      textAlign: 'center',
                      padding: '2rem 1rem',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease',
                      backgroundColor:'white'
                    }}>
                      <Card.Body>
                        <div style={{
                          width: '70px',
                          height: '70px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1.5rem',
                          color: 'white',
                          fontSize: '1.8rem'
                        }}>
                          <i className={benefit.icon}></i>
                        </div>
                        <h5 style={{ 
                          fontWeight: 600, 
                          marginBottom: '1rem',
                          color: '#1e293b'
                        }}>
                          {benefit.title}
                        </h5>
                        <p style={{ color: '#64748b' }}>
                          {benefit.description}
                        </p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section style={{
        position: 'relative',
        padding: '80px 0',
        background: 'linear-gradient(135deg, #1e293b 0%, #2d3748 100%)',
        color: 'white',
        overflow: 'hidden'
      }}>
        <motion.div 
          className="position-absolute rounded-circle"
          style={{
            width: '200px',
            height: '200px',
            top: '-50px',
            right: '-50px',
            background: 'rgba(255, 255, 255, 0.1)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <Container style={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 style={{ 
              fontWeight: 800, 
              marginBottom: '1.5rem',
              fontSize: '2.5rem'
            }}>
              Ready to Start Your Journey?
            </h2>
            <p style={{ 
              fontSize: '1.2rem', 
              marginBottom: '2rem',
              opacity: 0.9
            }}>
              Join SpireNest and help businesses transform with NetSuite solutions
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                size="lg"
                href="https://forms.office.com/r/XMsY5Aepfv"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #ff7a00 0%, #f59e0b 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 3rem',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '50px',
                  textDecoration: 'none',
                  boxShadow: '0 10px 20px rgba(255, 122, 0, 0.3)'
                }}
              >
                Apply Today <i className="bi bi-arrow-right ms-2"></i>
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
      <FAQSection />
    </div>
  );
};

export default Careers;